import { z } from 'zod'
import { keptSignatureMethods } from './signature'

/**
 * Tasks: a field of a form done on another device — photos or a file to attach, or a signature — by the one filling the
 * form on their portable screen (จอพกพา, screen.ts: now, while the form is open), or by someone else on their phone in
 * Thananan OS (งานรอทำ, opened by the notification: whenever, for a week). The field fills in: in the form while it is
 * open, and on the record itself when the form was for a saved one. Someone else is asked only for files: a signature
 * is done on one's own screen — one's own, or that of someone at the counter (`ask` says what they sign to).
 *
 * Asked by someone they take tasks from (`taskTrust` always) or by themselves, a task is open at once and notifies
 * them; asked by anyone else it waits quietly as a request until they take it (once, or from that person always) or
 * turn it down (once, or that person always).
 */
export const taskKinds = ['file', 'files', 'signature'] as const
export type TaskKind = (typeof taskKinds)[number]

/** requested: waits to be taken; open: to do; done; declined (by the one asked); cancelled (by who asked); expired */
export const taskStatuses = ['requested', 'open', 'done', 'declined', 'cancelled', 'expired'] as const
export type TaskStatus = (typeof taskStatuses)[number]
/** Whether a task still waits for its doer */
export const taskWaiting = (status: TaskStatus) => status === 'requested' || status === 'open'

/** How long a task waits: on one's screen, a day (the form is open now); someone else's, a week */
export const TASK_HOURS = { own: 24, others: 7 * 24 } as const
/** Files one task brings at most */
export const TASK_MAX_FILES = 10
/** Tasks one person may have waiting with another at a time */
export const TASK_MAX_WAITING = 20

/** What becomes of a task asked of someone: open now, waiting to be taken, or refused (they turned that person down) */
export function taskOpening(own: boolean, trust: 'always' | 'never' | null): 'open' | 'requested' | 'refused' {
  if (own || trust === 'always') return 'open'
  return trust === 'never' ? 'refused' : 'requested'
}

/** A key point with its figures marked (`termsParts` in tms), so the screen shows them bold and underlined */
export const screenPoint = z.array(z.object({ text: z.string().max(500), figure: z.boolean() })).max(40)
export type ScreenPoint = z.infer<typeof screenPoint>

/**
 * What someone at the counter signs to, shown on the portable screen above its pad: a sender accepting the shipping
 * terms, a receiver taking goods. None for one's own signature
 */
export const screenAsk = z.object({
  title: z.string().trim().min(1).max(200),
  /** Paragraphs saying what this is */
  intro: z.array(z.string().max(1000)).max(5).optional(),
  /** Key points, figures bold and underlined */
  points: z.array(screenPoint).max(20).optional(),
  /** What the signer agrees to, right above the pad */
  statement: z.string().max(1000).optional(),
})
export type ScreenAsk = z.infer<typeof screenAsk>

export const taskInput = z.object({
  kind: z.enum(taskKinds),
  assigneeId: z.string().min(1),
  /** The field as the form shows it: "สลิป / รูปเช็ค" */
  label: z.string().trim().min(1).max(120),
  /** What it is for: "ใบรับเงิน RV6910-0001" */
  title: z.string().trim().max(200).nullish(),
  note: z.string().trim().max(500).nullish(),
  /** Where it goes once done, when the form is for a saved record: that record's field (a file, or a list of them) */
  target: z.object({ resource: z.string().min(1), recordId: z.string().min(1), field: z.string().min(1) }).nullish(),
  /** A signature on one's own screen by someone at the counter: what they sign to */
  ask: screenAsk.nullish(),
})
export type TaskInput = z.infer<typeof taskInput>

/** What the doer hands in: the files they uploaded (a signature: its PNG, and how it was made) */
export const taskResultInput = z.object({
  fileIds: z.array(z.string().min(1)).min(1).max(TASK_MAX_FILES),
  signatureMethod: z.enum(keptSignatureMethods).nullish(),
})
export type TaskResultInput = z.infer<typeof taskResultInput>

export interface TaskPerson {
  id: string
  name: string
}

export interface Task {
  id: string
  kind: TaskKind
  status: TaskStatus
  label: string
  title: string | null
  note: string | null
  requestedBy: TaskPerson
  assignee: TaskPerson
  /** For a saved record: it is put there once done */
  target: { resource: string; recordId: string; field: string } | null
  /** Someone at the counter signs: what they sign to */
  ask: ScreenAsk | null
  /** What was handed in: the files, or the signature's image */
  fileIds: string[]
  signatureMethod: (typeof keptSignatureMethods)[number] | null
  /** When it was put on the record, or why it could not be */
  appliedAt: string | null
  applyError: string | null
  createdAt: string
  expiresAt: string
  doneAt: string | null
}

/** Someone one may ask, as the picker lists them */
export interface TaskPersonOption extends TaskPerson {
  /** Their nickname and position, to tell two of the same name apart (never their username) */
  detail: string | null
}
